import PropTypes from 'prop-types'
import { useEffect, useMemo, useState } from 'react'
import { FormLabel, Modal } from 'react-bootstrap'
import invoiceApi from 'api/invoiceApi'
import { setAppSpinning } from 'app/appSlice'
import CustomSelect from 'general/components/CustomSelect'
import Option from 'general/components/CustomSelect/CustomOption'
import MenuList from 'general/components/CustomSelect/MenuList'
import ToastHelper from 'general/helpers/ToastHelper'
import { useDispatch } from 'react-redux'
import './style.scss'
import ExcelJs from 'exceljs'
import {
  DELIVERY_BILL_UPLOAD_GUIDES,
  DELIVERY_BILL_INTERNAL_UPLOAD_HEADER_COLUMNS,
  INVOICE_UPLOAD_DATA_DELIVERY_BILL_SAMPLES,
  INVOICE_TEMPLATE_TYPES,
  DELIVERY_BILL_FOR_AGENT_UPLOAD_HEADER_COLUMNS,
} from 'general/constants/AppConstants'
import { filterColumnsBaseOnInvoiceTemplate } from 'general/helpers/ImportInvoiceHelper'

ModalUploadDeliveryBillExcel.prototype = {
  show: PropTypes.bool,
  setShow: PropTypes.func,
  content: PropTypes.string,
  handleConfirm: PropTypes.func,
  serials: PropTypes.array,
}

function ModalUploadDeliveryBillExcel(props) {
  const dispatch = useDispatch()

  const { show, setShow, serials } = props

  const templateInvoiceSerials = useMemo(
    () => serials?.filter(({ value }) => value) || [],
    [serials],
  )

  const [selectedInvoiceTemplate, setSelectedInvoiceTemplate] = useState()
  const [selectedFile, setSelectedFile] = useState()
  const [resultText, setResultText] = useState('')

  useEffect(() => {
    if (!templateInvoiceSerials?.length) return

    let firstInvoiceTemplate = templateInvoiceSerials.at(0)
    setSelectedInvoiceTemplate({
      invoiceTemplateId: firstInvoiceTemplate.invoiceTemplateId,
      label: firstInvoiceTemplate.serial,
      value: firstInvoiceTemplate.invoiceTemplateId,
      name: firstInvoiceTemplate.name,
      category: firstInvoiceTemplate.category,
      extend_fields: firstInvoiceTemplate.extend_fields,
      type: firstInvoiceTemplate.type,
    })
  }, [templateInvoiceSerials])

  const handleClose = () => {
    setShow(false)
    setResultText('')
    setSelectedInvoiceTemplate()
    setSelectedFile()
  }

  const handleSelectInvoiceSerial = (invoiceTemplate) => {
    setSelectedInvoiceTemplate(invoiceTemplate)
  }

  // Đặt tên cột excel
  function getExcelColumnName(index) {
    let name = ''
    while (index >= 0) {
      name = String.fromCharCode((index % 26) + 65) + name
      index = Math.floor(index / 26) - 1
    }
    return name
  }

  const handleUploadExcel = async () => {
    if (!selectedFile) {
      ToastHelper.showError('Chưa chọn file excel')
      return
    }

    if (!selectedInvoiceTemplate) {
      ToastHelper.showError('Chưa chọn mẫu phiếu xuất kho')
      return
    }

    let params = {
      invoiceTemplateId: selectedInvoiceTemplate.invoiceTemplateId,
      file: selectedFile,
    }

    dispatch(setAppSpinning(true))

    try {
      let res = await invoiceApi.upload(params)
      dispatch(setAppSpinning(false))
      let { successInvoiceSequences, failedInvoiceSequences } = res

      successInvoiceSequences = successInvoiceSequences.map((item) => ({
        ...item,
        success: 'Y',
      }))

      failedInvoiceSequences = failedInvoiceSequences.map((item) => ({
        ...item,
        success: 'N',
      }))

      let combineResult = successInvoiceSequences
        .concat(failedInvoiceSequences)
        .sort((a, b) => a.invoiceSequence - b.invoiceSequence)

      ToastHelper.showSuccess('Upload excel phiếu xuất kho thành công')
      setResultText(
        combineResult.map((item) => JSON.stringify(item)).join(',<br/>'),
      )
    } catch (e) {
      console.log(e)

      setResultText('')
      dispatch(setAppSpinning(false))
    } finally {
      if (typeof props?.refetchListInvoice == 'function') {
        props.refetchListInvoice()
      }
    }
  }

  const handleDownloadSampleExcel = async () => {
    if (!selectedInvoiceTemplate) {
      alert('Lỗi khi tìm kiếm serial')
      return
    }

    const fileName = selectedInvoiceTemplate.name + '.xlsx'
    const extendFields = selectedInvoiceTemplate?.extend_fields || []
    const buyerInfoExtendFields = extendFields.filter(
      ({ regionName }) => regionName == 'buyerInfo',
    )
    const tableDetailExtendFields = extendFields.filter(
      ({ regionName }) => regionName == 'tableDetail',
    )
    const commonFont = {
      name: 'Times New Roman',
      family: 4,
      size: 13,
    }
    const commonBorder = {
      top: { style: 'thin', color: { argb: '000000' } },
      left: { style: 'thin', color: { argb: '000000' } },
      bottom: { style: 'thin', color: { argb: '000000' } },
      right: { style: 'thin', color: { argb: '000000' } },
    }

    const wb = new ExcelJs.Workbook()
    function addWorkSheet({ invoiceTemplate, sheetName = 'Phiếu xuất kho' }) {
      const ws = wb.addWorksheet(sheetName)

      /* gen excel columns */
      const headerColumn =
        selectedInvoiceTemplate?.type ===
        INVOICE_TEMPLATE_TYPES.DELIVERY_BILL_INTERNAL
          ? DELIVERY_BILL_INTERNAL_UPLOAD_HEADER_COLUMNS
          : DELIVERY_BILL_FOR_AGENT_UPLOAD_HEADER_COLUMNS
      let excelColumns = headerColumn.filter(({ onlyWeb }) => onlyWeb !== true)

      /* add buyerInfo columns */
      let spi = excelColumns.findIndex(({ key }) => key == 'tenhang')
      excelColumns.splice(
        spi,
        0,
        ...buyerInfoExtendFields.map(({ label, name }) => ({
          header: label,
          key: name,
          width: 20,
          webWidth: '100px',
          description: label,
          mappingKeywords: label,
        })),
      )

      /* add tableDetail columns */
      excelColumns = excelColumns.concat(
        tableDetailExtendFields.map(({ label, name }) => ({
          header: label,
          key: name,
          width: 20,
          webWidth: '100px',
          description: label,
          mappingKeywords: label,
        })),
      )

      /* filter columns base on invoice template */
      excelColumns = filterColumnsBaseOnInvoiceTemplate({
        baseColumns: excelColumns,
        serial: invoiceTemplate,
      })

      let columnsLength = excelColumns.length

      // uploadGuides
      ws.mergeCells(`A1:${getExcelColumnName(columnsLength - 1)}1`)
      ws.getCell('A1').fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '99CCFF' },
      }
      ws.getCell('A1').value = DELIVERY_BILL_UPLOAD_GUIDES.title
      ws.getCell('A1').font = {
        ...commonFont,
        size: 16,
        bold: true,
      }

      ws.mergeCells(`A2:${getExcelColumnName(columnsLength - 1)}2`)
      ws.getCell('A2').fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFCC99' },
      }
      ws.getCell('A2').value = DELIVERY_BILL_UPLOAD_GUIDES.subtitle
      ws.getCell('A2').font = {
        ...commonFont,
        color: { argb: 'FF0000' },
      }

      let startIndex = 3
      DELIVERY_BILL_UPLOAD_GUIDES.guides.forEach((guide) => {
        ws.mergeCells(
          `A${startIndex}:${getExcelColumnName(
            columnsLength - 1,
          )}${startIndex}`,
        )
        ws.getCell(`A${startIndex}`).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFCC99' },
        }
        ws.getCell(`A${startIndex}`).value = guide
        ws.getCell(`A${startIndex}`).font = {
          ...commonFont,
        }
        startIndex++
      })

      // sampleData
      /* header */
      startIndex = 12
      ws.getRow(startIndex).font = {
        ...commonFont,
        bold: true,
      }
      ws.getRow(startIndex).values = excelColumns.map(({ header }) => header)

      let startProductIndex = ws
        .getRow(startIndex)
        .values.findIndex((item) => item == 'Tên hàng (*)')

      excelColumns.forEach((_, index) => {
        let cellName = `${getExcelColumnName(index)}${startIndex}`
        ws.getCell(cellName).border = commonBorder
        ws.getCell(cellName).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: {
            argb: index < startProductIndex - 1 ? 'CCCCFF' : 'FFFF00',
          },
        }
        ws.getCell(cellName).alignment = {
          vertical: 'middle',
          horizontal: 'center',
          wrapText: true,
        }
      })
      ws.columns = excelColumns.map(({ key, width }) => {
        return { key, width }
      })

      const INVOICE_UPLOAD_DATA_SAMPLES =
        INVOICE_UPLOAD_DATA_DELIVERY_BILL_SAMPLES.map((item) => ({
          ...item,
          ...(item.stt ? buyerInfoExtendFields : []).reduce(
            (result, { name, defaultValue }) => ({
              ...result,
              [name]: defaultValue ?? '',
            }),
            {},
          ),
          ...tableDetailExtendFields.reduce(
            (result, { name, defaultValue }) => ({
              ...result,
              [name]: defaultValue ?? '',
            }),
            {},
          ),
        }))

      /* data */
      startIndex = 13
      ws.addRows(INVOICE_UPLOAD_DATA_SAMPLES)
      ws.eachRow((row, rowNumber) => {
        if (rowNumber < startIndex) return

        row.eachCell((cell) => {
          cell.alignment = {
            vertical: 'middle',
            horizontal: 'center',
            wrapText: true,
          }
          cell.border = commonBorder
          cell.font = commonFont
          if (cell._column?.key != 'thanhtienhh') {
            cell.numFmt = '@'
          }
        })
      })
    }

    addWorkSheet({ invoiceTemplate: selectedInvoiceTemplate })

    await wb.xlsx.writeBuffer().then(function (data) {
      const blob = new Blob([data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      const url = window.URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = fileName
      anchor.click()
      window.URL.revokeObjectURL(url)
    })
  }

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      className="ModalUploadInvoiceExcel"
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <span>Upload excel phiếu xuất kho</span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div /* className="create-invoice-right-col" */>
          <div className="d-flex flex-row align-items-center w-100 mb-5">
            <FormLabel style={{ flexBasis: '80px' }} className="mb-1">
              Ký hiệu:
            </FormLabel>

            <CustomSelect
              classNamePrefix="rs-kh"
              placeholder={'Chọn ký hiệu'}
              currentValue={selectedInvoiceTemplate?.value}
              autoSize
              selectOptions={
                templateInvoiceSerials?.map((item) => ({
                  invoiceTemplateId: item.invoiceTemplateId,
                  label: item.serial,
                  value: item.invoiceTemplateId,
                  name: item.name,
                  category: item.category,
                  extend_fields: item.extend_fields,
                  type: item.type,
                })) ?? []
              }
              customFilterFn={(option, searchText) => {
                return (
                  option.label
                    ?.toLowerCase()
                    ?.includes(searchText.toLowerCase()) ||
                  option.data.name
                    ?.toLowerCase()
                    ?.includes(searchText.toLowerCase())
                )
              }}
              onChange={(selectedOption) =>
                handleSelectInvoiceSerial(selectedOption)
              }
              components={{
                MenuList,

                Option,
              }}
              menuParentRef={document.querySelector('.ModalUploadInvoiceExcel')}
            />
          </div>

          <div className="d-flex flex-row align-items-center w-100 mb-5">
            <FormLabel style={{ flexBasis: '80px' }} className="mb-1">
              Chọn file:
            </FormLabel>

            <input
              className="form-control flex-fill"
              type="file"
              style={{ width: 'auto' }}
              onChange={(e) => {
                setSelectedFile(e?.target?.files[0])
              }}
            ></input>
          </div>

          {resultText && <UploadInvoiceNotify text={resultText} />}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <div className="">
          <button
            className="btn btn-outline-warning mr-4"
            onClick={handleDownloadSampleExcel}
            disabled={!selectedInvoiceTemplate}
          >
            Tải file excel mẫu
          </button>

          <button
            className="btn btn-success px-8 mr-4"
            onClick={handleUploadExcel}
            disabled={!selectedInvoiceTemplate || !selectedFile}
          >
            Tải lên
          </button>

          <button
            className="btn btn-outline-secondary px-8"
            onClick={handleClose}
          >
            Đóng
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}

function UploadInvoiceNotify({ text }) {
  return (
    <div className="uin-container mt-5">
      <div className="uin-wrapper d-flex flex-row align-items-center">
        <i
          className="fa-sharp fa-2x fa-light fa-circle-info mr-2"
          style={{ color: '#044868' }}
        ></i>

        <span
          className="uin-text"
          dangerouslySetInnerHTML={{ __html: text }}
        ></span>
      </div>
    </div>
  )
}

export default ModalUploadDeliveryBillExcel
